<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useHead, useRoute } from 'nuxt/app'

  import { fontStyles, fontStylesheetHref } from '@ghl/clientportal-customization'

  import { provideSeoSiteProfile } from '@common/core-layer/composables/seo/useSeo'
  import { useLocation } from '@common/location-layer/composables/useLocation'
  import { usePortalCustomization } from '@common/location-layer/composables/usePortalCustomization'

  import { isCPStandaloneShellRoutePath } from '@clientportal/routing-layer/constants/auth.constants'
  import { isCPAuthRoutePath } from '@clientportal/routing-layer/utils/auth-route.utils'

  import { createClientPortalSeoSiteProfile } from './seo/clientPortalSeoProfile'

  const CPRootContainer = defineAsyncComponent(() => import('./components/CPRootContainer.vue'))

  const route = useRoute()
  const { locale } = useI18n()
  const { store: locationStore } = useLocation()
  const customization = usePortalCustomization()

  const documentLanguage = computed(() => {
    const normalizedLocale = locale.value.trim().replaceAll('_', '-')
    if (!normalizedLocale) return 'en'

    try {
      return Intl.getCanonicalLocales(normalizedLocale)[0] ?? 'en'
    } catch {
      return 'en'
    }
  })

  const isStandaloneCpShellRoute = computed(() => {
    const standaloneFromRouteMeta = Boolean(route.meta?.standaloneCpShell)
    const standaloneFromMatchedMeta =
      route.matched?.some(record => record.meta?.standaloneCpShell === true || record.meta?.standalonePublicRoute === true) ?? false
    const standaloneFromPath = isCPStandaloneShellRoutePath(route.path)
    return standaloneFromRouteMeta || standaloneFromMatchedMeta || standaloneFromPath
  })

  const layoutBoundaryKey = computed(() => (isCPAuthRoutePath(route.path) ? 'auth' : 'portal'))

  // Legal pages render outside CPRootContainer, so this provide is the one useSeo reads.
  // Capture the store here — Unhead can re-run the getter after the active Pinia is gone.
  provideSeoSiteProfile(() => createClientPortalSeoSiteProfile(locationStore))

  useHead(() => {
    // AI-built portals can pick their fonts; every other portal keeps the app's Inter.
    const fonts = customization.value?.theme?.fonts
    const fontsHref = fontStylesheetHref(fonts)
    const fontsCss = fontStyles(fonts)
    return {
      htmlAttrs: {
        lang: documentLanguage.value,
      },
      link: fontsHref ? [{ key: 'cp-customization-fonts', rel: 'stylesheet', href: fontsHref }] : [],
      style: fontsCss ? [{ key: 'cp-customization-fonts', textContent: fontsCss }] : [],
    }
  })
</script>

<template>
  <NuxtPage v-if="isStandaloneCpShellRoute" />
  <CPRootContainer v-else>
    <NuxtLayout :key="layoutBoundaryKey">
      <NuxtPage />
    </NuxtLayout>
  </CPRootContainer>
</template>
